import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { startLogin } from "@/lib/login";
import { DominoTile } from "@/components/DominoTile";
import { ShieldCheck, Zap, Trophy, Users, Wallet, PlayCircle } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1768839722117-da92d4acc9ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Landing() {
  const { user, loading } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [play, setPlay] = useState(false);

  useEffect(() => { if (!loading && user) navigate("/lobby", { replace: true }); }, [user, loading, navigate]);

  const go = () => (user ? navigate("/lobby") : navigate("/login"));

  const tiers = [1, 2, 5, 10, 20];
  const hParts = (branding?.landing_headline || "DOMINÓ ONLINE").split(" ");
  const hFirst = hParts[0];
  const hRest = hParts.slice(1).join(" ");

  return (
    <div className="dr-grain relative min-h-screen bg-[#090B0E] overflow-hidden">
      {/* Header */}
      <header className="relative z-30 max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt={branding.brand_name} className="h-10 w-auto object-contain" />
          ) : (
            <div className="w-10 h-10 rounded-lg btn-gold flex items-center justify-center font-head font-black text-xl">{(branding?.brand_name || "D")[0]}</div>
          )}
          <span className="font-head font-extrabold text-xl">{branding?.brand_name || "DOMINÓ ROYALLE"}</span>
        </div>
        <button onClick={() => navigate("/login")} data-testid="header-login-btn"
          className="px-5 py-2 rounded-full border border-[#D4AF37]/40 text-[#F3E5AB] font-semibold hover:bg-[#D4AF37]/10 transition-colors">
          Entrar
        </button>
      </header>

      {/* Hero */}
      <section className="relative z-20 max-w-7xl mx-auto px-6 pt-10 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] text-xs font-semibold mb-6">
            <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" /> Mesas 1x1 ao vivo agora
          </div>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            {hFirst} {hRest && <span className="gold-text">{hRest}</span>}
          </h1>
          <p className="mt-4 text-lg text-[#9CA3AF] font-head font-semibold">{branding?.landing_subtitle}</p>
          <p className="mt-3 text-[#9CA3AF] max-w-md">
            {branding?.landing_description}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button onClick={go} data-testid="play-now-btn"
              className="btn-gold px-8 py-4 rounded-full text-lg uppercase font-head flex items-center gap-2">
              <PlayCircle size={22} /> Jogar Agora
            </button>
            <button onClick={() => setPlay(true)} data-testid="how-to-btn"
              className="px-6 py-4 rounded-full border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors">
              Como Jogar
            </button>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {[["+28", "Pedras"], ["30s", "Por turno"], ["80%", "Prêmio"]].map(([a, b]) => (
              <div key={b} className="dr-card p-4 text-center">
                <div className="font-mono-num font-bold text-2xl gold-text">{a}</div>
                <div className="text-xs text-[#9CA3AF] mt-1">{b}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="absolute -inset-6 bg-[#D4AF37]/10 blur-3xl rounded-full" />
          <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/20 shadow-2xl">
            <img src={HERO_BG} alt="Mesa de dominó" className="w-full h-[360px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#090B0E] via-transparent to-transparent" />
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1">
              <DominoTile left={6} right={6} orientation="h" size={56} />
              <DominoTile left={6} right={3} orientation="h" size={56} />
              <DominoTile left={3} right={5} orientation="h" size={56} />
            </div>
          </div>
        </div>
      </section>

      {/* Stakes */}
      <section className="relative z-20 max-w-7xl mx-auto px-6 pb-16">
        <h2 className="font-head text-2xl sm:text-3xl font-bold mb-6">Salas de <span className="gold-text">Aposta</span></h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {tiers.map((t) => (
            <button key={t} onClick={go} data-testid={`landing-stake-${t}`}
              className="dr-card p-6 text-left group hover:border-[#D4AF37]/50 transition-colors">
              <div className="text-xs text-[#9CA3AF] uppercase tracking-wider">Entrada</div>
              <div className="font-mono-num font-bold text-3xl mt-1 group-hover:gold-text transition-colors">R$ {t}</div>
              <div className="mt-3 text-xs text-[#10B981] flex items-center gap-1"><Trophy size={12} /> Prêmio R$ {(t * 2 * 0.8).toFixed(2)}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="relative z-20 max-w-7xl mx-auto px-6 pb-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          [Zap, "Tempo Real", "Cada jogada sincronizada via WebSocket com cronômetro de 30s."],
          [ShieldCheck, "Anti-Cheat", "Servidor autoritativo: você nunca vê as pedras do adversário."],
          [Wallet, "Carteira Real", "Depósitos e saques via Pix/Cartão, saldo sempre no servidor."],
          [Users, "1x1 Justo", "7 pedras cada, cava com compra e prêmio de 80% da mesa."],
        ].map(([Icon, t, d]) => (
          <div key={t} className="dr-card p-6">
            <Icon className="text-[#D4AF37] mb-3" size={26} />
            <h3 className="font-head font-bold text-lg">{t}</h3>
            <p className="text-sm text-[#9CA3AF] mt-2">{d}</p>
          </div>
        ))}
      </section>

      <footer className="relative z-20 border-t border-white/5 py-8 text-center text-xs text-[#6b7280] px-6">
        <p>DOMINÓ ROYALLE — Jogo de habilidade com apostas em créditos. Jogue com responsabilidade. +18.</p>
        <p className="mt-2">A plataforma controla todo o saldo no servidor e não se responsabiliza por transferências diretas entre usuários.</p>
      </footer>

      {play && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setPlay(false)}>
          <div className="dr-card max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="how-to-modal">
            <h3 className="font-head text-2xl font-bold mb-4">Como Jogar — Dominó 1x1</h3>
            <ul className="space-y-2 text-sm text-[#d1d5db] list-disc pl-5">
              <li>Cada jogador recebe 7 pedras; as 14 restantes ficam na cava.</li>
              <li>Encaixe uma pedra numa das pontas da mesa quando for sua vez (30s por turno).</li>
              <li>Sem pedra jogável? Compre da cava. Só pode passar com a cava vazia.</li>
              <li>Vence quem bater (esvaziar a mão) ou tiver menos pontos no fechamento.</li>
              <li>O vencedor leva 80% da mesa direto na carteira.</li>
            </ul>
            <button onClick={() => setPlay(false)} className="btn-gold w-full mt-6 py-3 rounded-full">Entendi</button>
          </div>
        </div>
      )}
    </div>
  );
}
